<template>
    <aside class="the-sidebar" :class="{ expanded: expanded, 'is-hidden-mobile': hideOnMobile }">
        <div class="sidebar-inner">
            <div class="sidebar-back">
                <app-icon-button @click="expanded = !expanded">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" class="back-arrow">
                        <path d="M5.66606 8.53387L9.46606 12.2672C9.73273 12.5339 10.1327 12.5339 10.3994 12.2672C10.6661 12.0005 10.6661 11.6005 10.3994 11.3339L7.13273 8.00054L10.3994 4.66721C10.6661 4.40054 10.6661 4.00054 10.3994 3.73387C10.2661 3.60054 10.1327 3.53387 9.93273 3.53387C9.73273 3.53387 9.5994 3.60054 9.46606 3.73387L5.66606 7.46721C5.3994 7.80054 5.3994 8.20054 5.66606 8.53387Z" fill="currentColor"/>
                    </svg>
                </app-icon-button>
            </div>

            <div
                v-for="(item, i) in menuItems"
                :key="item.key"
                :class="{ 'mobile-hidden': item.mobileHidden }"
            >
                <div
                    class="sidebar-item"
                    :class="{ active: activeMenu === item.key }"
                    @click="handleMenuClick(item)"
                >
                    <img v-if="item.src" :src="item.src" :alt="item.key" class="sidebar-icon" />
                    <span class="sidebar-label">{{ categoryLabel(item.key) }}</span>
                </div>
                <div v-if="i < menuItems.length - 1" class="item-divider"></div>
            </div>
        </div>

        <div class="sidebar-shadow"></div>
    </aside>
</template>

<script>
import { categoryLabel } from '@/core/i18n';

// Menu items come from the `sidebar.menu` extension point. Items with a
// `load()` function (game categories) are expanded at runtime; category
// items switch the home page category instead of navigating.
export default {
    name: 'TheSidebar',
    data() {
        return {
            expanded: false,
            hideOnMobile: false,
            activeMenu: 'home',
            menuItems: []
        };
    },
    watch: {
        expanded(val) {
            document.documentElement.style.setProperty('--sidebar-width', val ? '240px' : '64px');
        },
        $route() {
            this.syncRouteToMenu();
        }
    },
    created() {
        this.menuItems = this.contributedItems()
            .filter(item => !item.load)
            .map(this.toStaticItem);
        this.syncRouteToMenu();
        document.documentElement.style.setProperty('--sidebar-width', this.expanded ? '240px' : '64px');
        this.loadMenuItems();
    },
    mounted() {
        this.$root.$on('category-change', this.onCategoryChange);
        this.$root.$on('mobile-provider-open', this.onMobileProviderOpen);
    },
    beforeDestroy() {
        this.$root.$off('category-change', this.onCategoryChange);
        this.$root.$off('mobile-provider-open', this.onMobileProviderOpen);
    },
    methods: {
        categoryLabel,
        contributedItems() {
            const { loggedIn, memberInfo } = this.$sharedState;
            return this.$modules.items('sidebar.menu', { loggedIn, memberInfo });
        },
        toStaticItem(item) {
            return { ...item, src: this.$asset(item.icon) };
        },
        async loadMenuItems() {
            const groups = await Promise.all(this.contributedItems().map(async item => {
                if (!item.load) return [this.toStaticItem(item)];
                const loaded = await item.load();
                return loaded.map(cat => ({ key: cat.key, src: cat.icon, category: true }));
            }));
            this.menuItems = groups.flat();
            const firstCategory = this.menuItems.find(item => item.category);
            if (this.activeMenu === 'home' && firstCategory) {
                this.activeMenu = firstCategory.key;
                this.$root.$emit('category-change', firstCategory.key);
            }
        },
        onCategoryChange(cat) {
            if (this.$route.path === '/') {
                this.activeMenu = cat;
            }
        },
        onMobileProviderOpen(open) {
            this.hideOnMobile = !!open;
        },
        handleMenuClick(item) {
            this.activeMenu = item.key;
            if (item.to) {
                if (this.$route.path !== item.to) this.$router.push(item.to);
                return;
            }
            if (!item.category) return;
            if (this.$route.path !== '/') {
                this.$router.push({ path: '/', query: { category: item.key } });
            } else {
                this.$root.$emit('category-change', item.key);
                if (this.$route.query.category !== item.key) {
                    this.$router.replace({ query: { ...this.$route.query, category: item.key } }).catch(() => {});
                }
            }
        },
        syncRouteToMenu() {
            if (this.$route.path === '/') {
                const cat = this.$route.query && this.$route.query.category;
                if (cat) this.activeMenu = cat;
            } else {
                const match = this.menuItems.find(item => item.to === this.$route.path);
                this.activeMenu = match ? match.key : '';
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.the-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 64px;
    z-index: 100;
    overflow: hidden;
    border-right: 1px solid var(--border-strong);
    background: linear-gradient(180deg, var(--surface-2) 0%, var(--surface-1) 46.63%, var(--surface-2) 100%);
    transition: width 0.3s ease;

    &.expanded {
        width: 240px;
    }

    @media (max-width: 768px) {
        position: relative;
        top: auto;
        left: auto;
        height: auto;
        width: 116px;
        flex-shrink: 0;
        border-right: none;
        border: 1px solid var(--border-subtle);
        border-radius: 10px;
        background: linear-gradient(180deg, var(--surface-2) 0%, var(--surface-1) 46.63%, var(--surface-2) 100%);
        box-shadow: 0 0 34px 0 var(--glow) inset;
        align-self: flex-start;
        transition: none;

        &.expanded {
            width: 116px;
        }

        &.is-hidden-mobile {
            display: none !important;
        }
    }
}

.sidebar-inner {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: 768px) {
        height: auto;
        overflow: visible;
    }
}

.sidebar-shadow {
    position: absolute;
    inset: 0;
    box-shadow: inset 0 0 44px 0 var(--glow);
    pointer-events: none;
    border-radius: inherit;
}

/* Back / toggle button row */
.sidebar-back {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 16px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        display: none;
    }
}


.back-arrow {
    transition: transform 0.3s ease;

    .the-sidebar:not(.expanded) & {
        transform: rotate(180deg);
    }
}

/* Menu item */
.sidebar-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0;
    cursor: pointer;
    border-radius: 8px;
    border: 1px solid transparent;
    padding: 11px 16px;
    transition: gap 0.3s ease;

    .the-sidebar.expanded & {
        gap: 14px;
    }

    @media (max-width: 768px) {
        gap: 6px;
        padding: 12px 8px 12px 16px;
    }

    &.active {
        background: linear-gradient(180deg, var(--surface-card) 0%, var(--surface-active) 48.56%, var(--surface-active) 100%);
        border-color: var(--border-accent);
    }
}

.sidebar-icon {
    flex-shrink: 0;
    object-fit: contain;
    width: 32px;
    height: 32px;

    @media (max-width: 768px) {
        width: 20px;
        height: 20px;
    }
}

.sidebar-label {
    color: var(--text);
    font-size: 16px;
    font-weight: 700;
    white-space: nowrap;
    opacity: 0;
    width: 0;
    overflow: hidden;
    transition: opacity 0.2s ease;

    .the-sidebar.expanded & {
        opacity: 1;
        width: auto;
    }

    @media (max-width: 768px) {
        opacity: 1;
        width: auto;
        font-size: 12px;
    }
}

.item-divider {
    display: none;

    @media (max-width: 768px) {
        display: block;
        width: 48px;
        height: 0;
        border-top: 1px solid var(--border-subtle);
        margin: 4px auto;
    }
}

.mobile-hidden {
    @media (max-width: 768px) {
        display: none;
    }
}
</style>
